import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
} from "react";
import { TextField, type TextFieldProps } from "@mui/material";
import { useLang } from "@/app/providers";
import {
  getLocaleDecimalSeparator,
  parseLocaleNumber,
  formatLocaleNumber,
} from "@/utils/number";

export interface LocaleNumberTextFieldProps extends Omit<
  TextFieldProps,
  "onChange" | "value"
> {
  /** The value of the field. Can be a number or string */
  value?: number | string | null;
  /** Explicit locale code (e.g., 'da', 'en'). Defaults to current language from useLang() */
  locale?: string;
  /** Whether to allow negative numbers. Defaults to true */
  allowNegative?: boolean;
  /** Whether to allow decimal fractions. Defaults to true */
  allowDecimals?: boolean;
  /** Maximum number of decimal places allowed */
  maxDecimals?: number;
  /** Callback fired when the value changes. Receives the synthetic change event and parsed number */
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    numericValue: number,
  ) => void;
  /** Optional callback fired with the parsed numeric value and raw string */
  onValueChange?: (numericValue: number, textValue: string) => void;
}

export const LocaleNumberTextField: React.FC<LocaleNumberTextFieldProps> = ({
  value,
  locale: propLocale,
  allowNegative = true,
  allowDecimals = true,
  maxDecimals,
  onChange,
  onValueChange,
  placeholder,
  ...restProps
}) => {
  const currentLang = useLang();
  const activeLocale = propLocale || currentLang || "en";
  const decimalSep = getLocaleDecimalSeparator(activeLocale);
  const otherSep = decimalSep === "," ? "." : ",";

  const prevLocaleRef = useRef(activeLocale);

  // Format initial / controlled value to the current locale
  const getFormattedValue = useCallback(
    (val: number | string | null | undefined, loc: string): string => {
      if (val === null || val === undefined) {
        return "";
      }
      return formatLocaleNumber(val, loc, { maxDecimals });
    },
    [maxDecimals],
  );

  const [displayValue, setDisplayValue] = useState<string>(() =>
    getFormattedValue(value, activeLocale),
  );

  // Synchronize when the external `value` prop changes
  useEffect(() => {
    const formatted = getFormattedValue(value, activeLocale);
    setDisplayValue((prev) => {
      // Avoid overwriting if numeric values are equal (e.g. typing "12," should not revert to "12")
      if (typeof value === "string") {
        const normalizedPrev = prev.replace(
          new RegExp(`\\${otherSep}`, "g"),
          decimalSep,
        );
        const normalizedIncoming = value.replace(
          new RegExp(`\\${otherSep}`, "g"),
          decimalSep,
        );
        if (normalizedPrev === normalizedIncoming) {
          return normalizedIncoming;
        }
      } else if (typeof value === "number") {
        const prevNumeric = parseLocaleNumber(prev, activeLocale);
        if (prevNumeric === value && prev !== "") {
          // Keep current text if user is typing (e.g. "12,0" or "12,")
          return prev;
        }
      }
      return formatted;
    });
  }, [value, activeLocale, decimalSep, otherSep, getFormattedValue]);

  // Handle locale changes (e.g., user toggles language between English and Danish)
  useEffect(() => {
    if (prevLocaleRef.current !== activeLocale) {
      prevLocaleRef.current = activeLocale;

      setDisplayValue((prev) => {
        if (!prev) return "";
        return formatLocaleNumber(prev, activeLocale, {
          maxDecimals,
        });
      });
    }
  }, [activeLocale, maxDecimals]);

  /**
   * Cleans and formats input text according to allowed rules:
   * - Replace alternative decimal separator (. -> , or , -> .)
   * - Enforce only one decimal separator if decimals allowed
   * - Enforce negative sign only at beginning if allowed
   * - Enforce max decimals
   */
  const sanitizeInput = useCallback(
    (input: string): string => {
      let cleaned = input;

      // Replace other separator with active decimal separator
      if (allowDecimals) {
        cleaned = cleaned.replace(new RegExp(`\\${otherSep}`, "g"), decimalSep);
      } else {
        cleaned = cleaned.replace(
          new RegExp(`[\\${decimalSep}\\${otherSep}]`, "g"),
          "",
        );
      }

      // Check negative sign
      let isNegative = false;
      if (allowNegative && cleaned.startsWith("-")) {
        isNegative = true;
      }

      // Remove all characters except digits and decimal separator
      const escapeSep = decimalSep === "." ? "\\." : ",";
      cleaned = cleaned.replace(new RegExp(`[^0-9${escapeSep}]`, "g"), "");

      // Handle multiple decimal separators: keep only the first one
      if (allowDecimals && cleaned.includes(decimalSep)) {
        const parts = cleaned.split(decimalSep);
        const integerPart = parts[0];
        let fractionPart = parts.slice(1).join("");
        if (maxDecimals !== undefined) {
          fractionPart = fractionPart.slice(0, maxDecimals);
        }
        cleaned = `${integerPart}${decimalSep}${fractionPart}`;
      }

      return isNegative ? `-${cleaned}` : cleaned;
    },
    [allowDecimals, allowNegative, decimalSep, otherSep, maxDecimals],
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const sanitized = sanitizeInput(raw);

    setDisplayValue(sanitized);

    const numericValue = parseLocaleNumber(sanitized, activeLocale);

    if (onChange) {
      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          value: sanitized,
        },
      } as ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent, numericValue);
    }

    if (onValueChange) {
      onValueChange(numericValue, sanitized);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = (
      event.clipboardData ||
      (window as unknown as { clipboardData: DataTransfer }).clipboardData
    )?.getData("text");

    if (!pastedText) return;

    event.preventDefault();
    const sanitized = sanitizeInput(pastedText);

    setDisplayValue(sanitized);

    const numericValue = parseLocaleNumber(sanitized, activeLocale);

    if (onChange) {
      const syntheticEvent = {
        target: { value: sanitized },
        currentTarget: { value: sanitized },
      } as ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent, numericValue);
    }

    if (onValueChange) {
      onValueChange(numericValue, sanitized);
    }
  };

  // Localize placeholder if it has a decimal format like "0.00"
  const localizedPlaceholder =
    typeof placeholder === "string" && placeholder.includes(otherSep)
      ? placeholder.replace(new RegExp(`\\${otherSep}`, "g"), decimalSep)
      : placeholder;

  return (
    <TextField
      type="text"
      inputMode="decimal"
      placeholder={localizedPlaceholder}
      value={displayValue}
      onChange={handleChange}
      onPaste={handlePaste}
      {...restProps}
    />
  );
};
